import React, { useState, useEffect } from 'react'
import styles from './LoadingsPanel.module.scss'
import moment from 'moment'
import { getLoadsTimeline } from 'service/LoadService'

const formatTime = (t) => {
  if (!t) return '—'
  const parts = []
  if (t.days > 0) parts.push(`${t.days}д`)
  if (t.hours > 0) parts.push(`${t.hours}ч`)
  if (t.minutes > 0) parts.push(`${t.minutes}м`)
  if (parts.length === 0) parts.push(t.seconds > 0 ? `${t.seconds}с` : '—')
  return parts.join(' ')
}

const LoadingsPanel = ({ loadings, driversStatus }) => {
  // ── Timeline state ──────────────────────────────────────────────────────────
  const todayStr = new Date().toISOString().split('T')[0]
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0] }
  const [timeline, setTimeline] = useState(null)
  const [tlDateFrom, setTlDateFrom] = useState(daysAgo(14))
  const [tlDateTo, setTlDateTo] = useState(todayStr)
  const [tlPreset, setTlPreset] = useState('15d')
  const [tlLoading, setTlLoading] = useState(false)

  const TL_PRESETS = [
    { key: '15d',  label: '15 дней',     days: 14 },
    { key: '30d',  label: 'За месяц',   days: 29 },
    { key: '90d',  label: '3 месяца',   days: 89 },
    { key: '180d', label: '6 месяцев',  days: 179 },
    { key: '365d', label: 'Год',        days: 364 },
  ]

  const applyPreset = (preset) => {
    const from = daysAgo(preset.days)
    setTlDateFrom(from)
    setTlDateTo(todayStr)
    setTlPreset(preset.key)
    loadTimeline(from, todayStr)
  }

  const loadTimeline = async (from, to) => {
    setTlLoading(true)
    try {
      const res = await getLoadsTimeline({ date_from: from, date_to: to })
      setTimeline(res.data)
    } catch (err) { console.error(err) }
    finally { setTlLoading(false) }
  }

  useEffect(() => {
    loadTimeline(daysAgo(14), todayStr)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Existing derived data ────────────────────────────────────────────────────
  const stats = {
    inLoading: loadings?.loadsSummary?.in_loading || 0,
    inRoute: loadings?.loadsSummary?.in_route || 0,
    completed: loadings?.loadsSummary?.completed || 0,
  }
  const total = stats.inLoading + stats.inRoute + stats.completed
  const processedReqs = loadings?.processedRequests || { total: 0, driver_registrations: 0, base_requests: 0 }
  const baseTypeStats = loadings?.baseTypeStats || { top_base: 0, chpp: 0, private_point: 0 }

  const byBase = (loadings?.baseStats || [])
    .map(b => ({
      name: b.name,
      inLoading: b.in_loading || 0,
      inRoute: b.in_route || 0,
      completed: b.completed || 0,
    }))
    .sort((a, b) => (b.inLoading + b.inRoute + b.completed) - (a.inLoading + a.inRoute + a.completed))

  const driverStatusItems = driversStatus ? [
    { name: 'В погрузке', value: driversStatus.in_loading || 0, color: '#f59e0b' },
    { name: 'В пути', value: driversStatus.in_route || 0, color: '#3b82f6' },
    { name: 'Свободно', value: driversStatus.free || 0, color: '#10b981' },
    { name: 'Заблокировано', value: driversStatus.blocked || 0, color: '#ef4444' },

  ] : []

  const dt = loadings?.delivery_time
  const lt = loadings?.loading_time
  const hasDelivery = dt && (dt.longest || dt.shortest || dt.median)
  const hasLoading = lt && (lt.longest || lt.shortest || lt.median)

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Погрузки</h2>
        <span className={styles.liveTag}>LIVE</span>
      </div>

      {/* KPI row */}
      <div className={styles.kpiRow}>
        <div className={`${styles.kpiCard} ${styles.kpiAmber}`}>
          <span className={styles.kpiLabel}>В погрузке</span>
          <span className={styles.kpiValue}>{stats.inLoading}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiBlue}`}>
          <span className={styles.kpiLabel}>В пути</span>
          <span className={styles.kpiValue}>{stats.inRoute}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiGreen}`}>
          <span className={styles.kpiLabel}>Завершено</span>
          <span className={styles.kpiValue}>{stats.completed}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiNeutral}`}>
          <span className={styles.kpiLabel}>Всего</span>
          <span className={styles.kpiValue}>{total}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiTeal}`}>
          <span className={styles.kpiLabel}>Обработано заявок</span>
          <span className={styles.kpiValue}>{1417 + (processedReqs.total || 0)}</span>
          <div className={styles.kpiSubrow}>
            <span className={styles.kpiSub}>Водители: {1252 + (processedReqs.driver_registrations || 0)}</span>
            <span className={styles.kpiSub}>Базы: {219 + (processedReqs.base_requests || 0)}</span>
          </div>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiOrange}`}>
          <span className={styles.kpiLabel}>Топливные базы</span>
          <span className={styles.kpiValue}>{baseTypeStats.top_base}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiRed}`}>
          <span className={styles.kpiLabel}>ТЭЦ</span>
          <span className={styles.kpiValue}>{baseTypeStats.chpp}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiPurple}`}>
          <span className={styles.kpiLabel}>Соц объекты и МСУ</span>
          <span className={styles.kpiValue}>{baseTypeStats.private_point}</span>
        </div>
      </div>

      {/* Mid row: driver statuses + time metrics */}
      <div className={styles.midRow}>
        {/* Driver statuses */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>Статус водителей</div>
          <div className={styles.driverStatusList}>
            {driverStatusItems.map(item => (
              <div key={item.name} className={styles.driverStatusItem}>
                <div className={styles.driverStatusName}>
                  <span
                    className={styles.dot}
                    style={{ background: item.color, boxShadow: `0 0 6px ${item.color}80` }}
                  />
                  <span>{item.name}</span>
                </div>
                <span className={styles.driverStatusCount}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Time metrics */}
        {(hasDelivery || hasLoading) && (
          <div className={styles.card}>
            <div className={styles.cardTitle}>Временные показатели</div>
            <div className={styles.timeMetricsGrid}>
              {hasDelivery && (
                <div className={styles.timeSection}>
                  <div className={styles.timeSectionTitle}>В пути — Кара-Кече → База</div>
                  <div className={styles.timeTable}>
                    <div className={styles.timeRow}>
                      <span className={styles.timeRowLabel}>Максимум</span>
                      <span className={styles.timeRowValue}>{formatTime(dt.longest)}</span>
                    </div>
                    <div className={styles.timeRow}>
                      <span className={styles.timeRowLabel}>Среднее</span>
                      <span className={styles.timeRowValue}>{formatTime(dt.median)}</span>
                    </div>
                    <div className={styles.timeRow}>
                      <span className={styles.timeRowLabel}>Минимум</span>
                      <span className={styles.timeRowValue}>{formatTime(dt.shortest)}</span>
                    </div>
                  </div>
                </div>
              )}
              {hasLoading && (
                <div className={styles.timeSection}>
                  <div className={styles.timeSectionTitle}>Погрузка — въезд → выезд</div>
                  <div className={styles.timeTable}>
                    <div className={styles.timeRow}>
                      <span className={styles.timeRowLabel}>Максимум</span>
                      <span className={styles.timeRowValue}>{formatTime(lt.longest)}</span>
                    </div>
                    <div className={styles.timeRow}>
                      <span className={styles.timeRowLabel}>Среднее</span>
                      <span className={styles.timeRowValue}>{formatTime(lt.median)}</span>
                    </div>
                    <div className={styles.timeRow}>
                      <span className={styles.timeRowLabel}>Минимум</span>
                      <span className={styles.timeRowValue}>{formatTime(lt.shortest)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── Timeline ─────────────────────────────────────────────────────── */}
      <div className={styles.tlSection}>
        {/* Top row: stats + date picker */}
        <div className={styles.tlTop}>
          <div className={styles.tlStats}>
            <div className={styles.tlStat}>
              <span className={styles.tlStatLabel}>С первого сканирования</span>
              <span className={styles.tlStatDate}>
                {timeline?.first_scan_date ? moment(timeline.first_scan_date).format('DD.MM.YYYY') : '—'}
              </span>
            </div>
            <div className={styles.tlStatDivider} />
            <div className={styles.tlStat}>
              <span className={styles.tlStatLabel}>Всего погрузок</span>
              <span className={styles.tlStatBig}>
                {timeline ? timeline.total_scanned.toLocaleString('ru-RU') : '—'}
              </span>
            </div>
          </div>
          <div className={styles.tlControls}>
            <div className={styles.tlPresets}>
              {TL_PRESETS.map(p => (
                <button
                  key={p.key}
                  className={`${styles.tlPresetBtn}${tlPreset === p.key ? ` ${styles.tlPresetBtnActive}` : ''}`}
                  onClick={() => applyPreset(p)}
                  disabled={tlLoading}
                >{p.label}</button>
              ))}
            </div>
            <div className={styles.tlDateRange}>
              <label className={styles.tlLabel}>с</label>
              <input
                type="date"
                value={tlDateFrom}
                max={tlDateTo}
                onChange={e => { setTlDateFrom(e.target.value); setTlPreset(null) }}
                className={styles.tlDateInput}
              />
              <label className={styles.tlLabel}>по</label>
              <input
                type="date"
                value={tlDateTo}
                min={tlDateFrom}
                max={todayStr}
                onChange={e => { setTlDateTo(e.target.value); setTlPreset(null) }}
                className={styles.tlDateInput}
              />
              <button
                className={styles.tlRefreshBtn}
                onClick={() => loadTimeline(tlDateFrom, tlDateTo)}
                disabled={tlLoading}
              >
                {tlLoading ? '…' : '↻'}
              </button>
            </div>
          </div>
        </div>

        {/* SVG bar chart */}
        <div className={styles.tlChartWrap}>
          {(() => {
            const data = timeline?.timeline || []
            const maxVal = Math.max(...data.map(d => d.total), 1)
            const W = 1000, H = 200
            const padL = 44, padR = 16, padT = 20, padB = 42
            const chartW = W - padL - padR
            const chartH = H - padT - padB
            const n = data.length
            const slotW = n > 0 ? chartW / n : chartW
            const barW = Math.max(4, slotW - 4)

            if (!tlLoading && data.length === 0) {
              return <div className={styles.tlEmpty}>Нет данных за выбранный период</div>
            }
            return (
              <svg viewBox={`0 0 ${W} ${H}`} className={styles.tlSvg} preserveAspectRatio="none">
                {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
                  const y = padT + chartH * (1 - ratio)
                  const val = Math.round(maxVal * ratio)
                  return (
                    <g key={ratio}>
                      <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e2e8f0" strokeWidth="1" />
                      <text x={padL - 6} y={y + 4} textAnchor="end" fontSize="11" fill="#94a3b8" fontFamily="inherit">{val}</text>
                    </g>
                  )
                })}
                {data.map((d, i) => {
                  const x = padL + i * slotW + (slotW - barW) / 2
                  const bH = d.total > 0 ? Math.max(2, (d.total / maxVal) * chartH) : 2
                  const y = padT + chartH - bH
                  const label = moment(d.date).format('DD.MM')
                  const isWe = [0, 6].includes(new Date(d.date + 'T12:00:00').getDay())
                  const show = n <= 31 || i % Math.ceil(n / 25) === 0
                  const fill = d.total === 0 ? '#e2e8f0' : isWe ? '#7c8db0' : '#0f172a'
                  return (
                    <g key={d.date}>
                      <rect x={x} y={y} width={barW} height={bH} rx="2" fill={fill}>
                        <title>{`${moment(d.date).format('DD.MM.YYYY')}: ${d.total} погр.`}</title>
                      </rect>
                      {d.total > 0 && bH > 18 && (
                        <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize="9" fill="#555" fontFamily="inherit">{d.total}</text>
                      )}
                      {show && (
                        <text
                          x={x + barW / 2} y={H - padB + 14}
                          textAnchor="end" fontSize="10"
                          fill={isWe ? '#7c8db0' : '#94a3b8'} fontFamily="inherit"
                          transform={`rotate(-40, ${x + barW / 2}, ${H - padB + 14})`}
                        >{label}</text>
                      )}
                    </g>
                  )
                })}
                <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="#e2e8f0" strokeWidth="1" />
              </svg>
            )
          })()}
          {tlLoading && <div className={styles.tlOverlay}>Загрузка...</div>}
        </div>
      </div>

      {/* Base stats table */}
      {byBase.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Статистика по базам</div>
          <div className={styles.baseTable}>
            <div className={styles.baseTableHead}>
              <span className={styles.baseColName}>База</span>
              <span className={styles.baseColNum}>В погрузке</span>
              <span className={styles.baseColNum}>В пути</span>
              <span className={styles.baseColNum}>Завершено</span>
            </div>
            {byBase.map((base, i) => (
              <div key={base.name} className={`${styles.baseTableRow} ${i % 2 === 1 ? styles.even : ''}`}>
                <span className={styles.baseColName}>{base.name}</span>
                <span className={`${styles.baseColNum} ${styles.numAmber}`}>{base.inLoading || '—'}</span>
                <span className={`${styles.baseColNum} ${styles.numBlue}`}>{base.inRoute || '—'}</span>
                <span className={`${styles.baseColNum} ${styles.numGreen}`}>{base.completed || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LoadingsPanel
